import { useEffect, useRef, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Send, Search, Check, CheckCheck, AlertTriangle, FileText, MessageSquare, Clock, ArrowDown } from "lucide-react";

const fmtTime = (ts) => {
  if (!ts) return "";
  try { const d = new Date(ts); return d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
};
// Delivery ticks come ONLY from Meta webhook statuses stored on the message (sent → delivered → read / failed)
const StatusIcon = ({ s, reason }) => {
  if (s === "read") return <span className="inline-flex items-center gap-0.5 text-sky-400" title="Read / Seen" data-testid="wa-status-read"><CheckCheck size={13} /><span className="text-[9px] font-medium">Seen</span></span>;
  if (s === "delivered") return <span className="inline-flex text-white/60" title="Delivered" data-testid="wa-status-delivered"><CheckCheck size={13} /></span>;
  if (s === "sent") return <span className="inline-flex text-white/60" title="Sent" data-testid="wa-status-sent"><Check size={13} /></span>;
  if (s === "failed") return <span className="inline-flex items-center gap-0.5 text-red-400" title={reason || "Failed"} data-testid="wa-status-failed"><AlertTriangle size={12} /><span className="text-[9px] font-medium">Failed</span></span>;
  if (s === "pending") return <span className="inline-flex text-white/40" title="Pending — WhatsApp not configured" data-testid="wa-status-pending"><Clock size={12} /></span>;
  return null;
};

export function WhatsAppLiveChat() {
  const { user } = useAuth();
  const { branches } = useBranch();
  const isAdmin = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const canSend = isAdmin || !!(user?.permissions || {}).whatsapp_chat_send;
  const [branchId, setBranchId] = useState(isAdmin ? "all" : (user?.branch_id || ""));
  const [chats, setChats] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);       // mobile
  const [thread, setThread] = useState(null);       // {name, branch_name, messages}
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "";

  const loadChats = useCallback(async () => {
    try {
      const { data } = await api.get("/wa-chat/chats", { params: { branch_id: branchId, q } });
      setChats(data);
    } catch (e) { /* silent poll */ }
  }, [branchId, q]);

  // Scroll behaviour: jump to bottom only on first open / own send, or when a new message arrives while the
  // reader is already near the bottom. Otherwise keep the exact position and offer a "Jump to latest" pill.
  const threadRef = useRef(null);
  const nearBottomRef = useRef(true);
  const lastCountRef = useRef(0);
  const lastSigRef = useRef("");
  const openedRef = useRef(null);
  const [newBelow, setNewBelow] = useState(0);
  const isNearBottom = () => { const el = threadRef.current; return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80; };
  const scrollToBottom = (behavior = "smooth") => { bottomRef.current?.scrollIntoView({ behavior }); setNewBelow(0); };
  const onThreadScroll = () => { nearBottomRef.current = isNearBottom(); if (nearBottomRef.current) setNewBelow(0); };

  const loadThread = useCallback(async (mobile, { force = false } = {}) => {
    if (!mobile) return;
    try {
      const { data } = await api.get(`/wa-chat/chats/${mobile}`);
      const count = (data.messages || []).length;
      const firstOpen = openedRef.current !== mobile;
      const wasNearBottom = isNearBottom();   // measured right before the DOM updates
      const prevCount = firstOpen ? 0 : lastCountRef.current;
      // skip the re-render entirely when nothing changed (polling / status refresh) — zero DOM churn, zero scroll movement
      const sig = JSON.stringify(data.messages || []);
      if (!firstOpen && !force && sig === lastSigRef.current) return;
      lastSigRef.current = sig;
      setThread(data);
      openedRef.current = mobile; lastCountRef.current = count;
      if (firstOpen || force) { nearBottomRef.current = true; setTimeout(() => scrollToBottom(firstOpen ? "auto" : "smooth"), 50); return; }
      if (count > prevCount) {
        if (wasNearBottom) setTimeout(() => scrollToBottom("smooth"), 50);
        else setNewBelow((n) => n + (count - prevCount));
      }
      // status-only updates (ticks) never move the scroll position
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);
  // live updates: poll list every 8s, active thread every 5s
  useEffect(() => {
    const t = setInterval(loadChats, 8000);
    return () => clearInterval(t);
  }, [loadChats]);
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => loadThread(active), 5000);
    return () => clearInterval(t);
  }, [active, loadThread]);

  const openChat = (mobile) => { setActive(mobile); loadThread(mobile); setChats((cs) => cs.map((c) => c.mobile === mobile ? { ...c, unread: 0 } : c)); };

  const send = async () => {
    if (!draft.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/wa-chat/chats/${active}/send`, { text: draft.trim() });
      setDraft(""); await loadThread(active, { force: true }); loadChats();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-3 h-[70vh]" data-testid="wa-livechat">
      {/* LEFT: conversation list */}
      <div className="border border-border rounded-lg bg-card flex flex-col overflow-hidden min-w-0">
        <div className="p-3 border-b border-border space-y-2">
          {isAdmin ? (
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); setActive(null); setThread(null); }}>
              <SelectTrigger className="bg-secondary h-9" data-testid="wa-chat-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className="border-primary text-primary" data-testid="wa-chat-branch-fixed">{branchName(user?.branch_id) || "Your Branch"}</Badge>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or mobile" className="bg-secondary pl-7 h-9" data-testid="wa-chat-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="wa-chat-list">
          {chats.length === 0 && <p className="p-4 text-sm text-muted-foreground text-center">No conversations</p>}
          {chats.map((c) => (
            <button key={c.mobile} type="button" onClick={() => openChat(c.mobile)} data-testid={`wa-chat-item-${c.mobile}`}
              className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-secondary transition-colors ${active === c.mobile ? "bg-secondary" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate flex items-center gap-1.5" data-testid={`wa-chat-name-${c.mobile}`}>{c.name || c.mobile}
                  {c.contact_type === "staff" && <span className="text-[9px] uppercase tracking-wide rounded px-1 py-px bg-amber-500/20 text-amber-300 shrink-0" data-testid={`wa-chat-badge-${c.mobile}`}>Staff</span>}
                  {c.contact_type === "customer" && <span className="text-[9px] uppercase tracking-wide rounded px-1 py-px bg-primary/20 text-primary shrink-0" data-testid={`wa-chat-badge-${c.mobile}`}>Customer</span>}
                </span>
                <span className="text-[10px] text-muted-foreground shrink-0">{fmtTime(c.last_ts)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <span className="text-xs text-muted-foreground truncate">{c.last_direction === "out" ? "↩ " : ""}{c.last_text}</span>
                {c.unread > 0 && <span className="shrink-0 bg-primary text-primary-foreground text-[10px] rounded-full px-1.5 py-0.5" data-testid={`wa-chat-unread-${c.mobile}`}>{c.unread}</span>}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] font-mono text-muted-foreground">{c.mobile}</span>
                {c.branch_name && <span className="text-[10px] text-primary/80">· {c.branch_name}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT: conversation thread */}
      <div className="border border-border rounded-lg bg-[#0b1410] flex flex-col overflow-hidden min-w-0">
        {!thread ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2">
            <MessageSquare size={40} className="opacity-40" />
            <p className="text-sm">Select a conversation</p>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-border bg-card flex items-center justify-between">
              <div className="min-w-0">
                <p className="font-medium text-sm truncate" data-testid="wa-thread-name">{thread.name || thread.mobile}{thread.contact_type === "staff" ? " · Staff" : thread.contact_type === "customer" ? " · Customer" : ""}</p>
                <p className="text-[11px] text-muted-foreground" data-testid="wa-thread-mobile">{thread.mobile}{thread.branch_name ? ` · ${thread.branch_name}` : ""}</p>
              </div>
            </div>
            <div ref={threadRef} onScroll={onThreadScroll} className="flex-1 overflow-y-auto p-4 space-y-2 relative" style={{ overflowAnchor: "none" }} data-testid="wa-chat-thread">
              {thread.messages.map((m, i) => (
                <div key={m.provider_id || `${m.ts}-${i}`} className={`flex ${m.direction === "in" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[78%] rounded-lg px-3 py-2 text-[13px] ${m.direction === "in" ? "bg-[#1f2c33] text-white" : "bg-[#005c4b] text-white"}`} data-testid={`wa-msg-${i}`}>
                    {m.kind === "invoice" && (
                      <div className="mb-1 flex items-center gap-2 rounded bg-black/25 px-2 py-1">
                        <FileText size={14} className="text-primary" />
                        <div className="leading-tight">
                          <p className="text-[11px] font-medium">{m.invoice_no ? `Invoice ${m.invoice_no}` : "Invoice"}{m.amount != null ? ` · ₹${Number(m.amount).toLocaleString("en-IN")}` : ""}</p>
                          {m.invoice_url && <a href={m.invoice_url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-300 underline" data-testid={`wa-invoice-link-${i}`}>View Invoice</a>}
                        </div>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap break-words">{m.text}</p>
                    <div className="flex items-center justify-end gap-1 mt-0.5 text-[10px] text-white/50">
                      {m.sent_by && <span className="mr-1">{m.sent_by}</span>}
                      {m.kind && m.kind !== "chat" && m.kind !== "ai" && m.kind !== "manual" && <span className="mr-1 uppercase">{m.kind}</span>}
                      <span>{fmtTime(m.ts)}</span>
                      {m.direction === "out" && <StatusIcon s={m.status} reason={m.reason} />}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {newBelow > 0 && (
              <div className="relative">
                <button type="button" onClick={() => scrollToBottom("smooth")} data-testid="wa-jump-latest"
                  className="absolute -top-12 left-1/2 -translate-x-1/2 z-10 rounded-full bg-primary text-primary-foreground shadow-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:bg-[#D4AF37]">
                  <ArrowDown size={13} /> {newBelow} new message{newBelow === 1 ? "" : "s"} · Jump to latest
                </button>
              </div>
            )}
            {canSend ? (
              <div className="p-3 border-t border-border bg-card flex items-center gap-2">
                <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message…" className="bg-secondary" data-testid="wa-chat-input" />
                <Button onClick={send} disabled={sending || !draft.trim()} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="wa-chat-send">
                  <Send size={15} />
                </Button>
              </div>
            ) : (
              <div className="p-3 border-t border-border bg-card text-center text-xs text-muted-foreground">You don't have permission to send messages.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
